import { useState, type FormEvent } from "react";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { updateOwnPassword, type AuthedRequester } from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// Shared set/change-password form used by both the guided-onboarding interstitial
// and the /security Password card (DRY, BRK-S026). "set" omits the current
// password (an invite-only broker who has none yet); "change" requires it. The
// live requirements checklist and the single passwordPolicy source are reused;
// Cognito stays the authoritative validator (a policy violation surfaces as the
// handled PMS { error } message).
export function SetPasswordForm({
  mode,
  request,
  onSuccess,
  submitLabel,
}: {
  mode: "set" | "change";
  request: AuthedRequester;
  onSuccess: () => void;
  submitLabel?: string;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      await updateOwnPassword(request, {
        ...(mode === "change" ? { currentPassword } : {}),
        newPassword,
      });
      onSuccess();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      {mode === "change" && (
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
      )}
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
      />
      <PasswordRequirements value={newPassword} />
      <TextField
        label="Confirm new password"
        type="password"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
      />
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      <SubmitButton pending={pending} pendingLabel="Saving…">
        {submitLabel ??
          (mode === "change" ? "Change password" : "Set password")}
      </SubmitButton>
    </form>
  );
}
